import { useMemo, useState } from "react";
import type { Session } from "../lib/session";
import { encodeShareLink, downloadSession, URL_SIZE_LIMIT } from "../lib/session";

interface Props {
  session: Session;
  onClose: () => void;
}

export function ShareModal({ session, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const share = useMemo(
    () => encodeShareLink(session, window.location.origin + window.location.pathname),
    [session],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard API can fail under non-secure contexts; users can
      // still select + copy by hand.
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Share "{session.title}"</h2>
        <div className="subtitle">
          Anyone with this link can open this channel — same trust as the
          export file, just over a URL.
        </div>

        {share.oversized && (
          <div className="share-warn">
            This channel is large ({Math.round(share.size / 1024)} KB encoded — over the {Math.round(URL_SIZE_LIMIT / 1024)} KB
            soft cap). The link works, but some chat and email apps may truncate it.
            Consider <button className="link-btn" onClick={() => downloadSession(session)}>exporting the file</button> instead.
          </div>
        )}

        <div className="field">
          <label>Invite link</label>
          <textarea
            className="share-url"
            value={share.url}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            rows={3}
          />
        </div>

        <div className="actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={copy}>
            {copied ? "Copied ✓" : "Copy link"}
          </button>
        </div>
      </div>
    </div>
  );
}
